/**
 * Presentation helper for a stopped concierge reply. Kept Firebase-free (unlike `ChatMessageModel`,
 * which pulls in the Firestore client) so it can be imported as a value from both the web message
 * card and the React Native message list without dragging Firebase into the RN jest runtime.
 */

/**
 * Builds the caption shown on a stopped reply. With a known generation duration it reads
 * "You stopped after 2s." (whole seconds, rolling over to "1m 30s" past a minute); without one it
 * falls back to "You stopped this response."
 *
 * @example
 * formatStoppedResponseLabel(2300); // "You stopped after 2s."
 * formatStoppedResponseLabel(90_000); // "You stopped after 1m 30s."
 * formatStoppedResponseLabel(undefined); // "You stopped this response."
 */
export function formatStoppedResponseLabel(durationMs?: number): string {
  if (durationMs === undefined || !Number.isFinite(durationMs) || durationMs < 0) {
    return 'You stopped this response.';
  }
  // Floor at 1s: a sub-second stop still reads as "after 1s" rather than a bare "0s".
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  const remainderSeconds = totalSeconds % 60;
  const label =
    totalSeconds < 60
      ? `${totalSeconds}s`
      : `${Math.floor(totalSeconds / 60)}m${remainderSeconds ? ` ${remainderSeconds}s` : ''}`;
  return `You stopped after ${label}.`;
}
